import { z } from 'zod';

/**
 * The diagram frame the agent's `emit_diagram` tool writes, as conversation-service relays it on the
 * `diagram` SSE event and stores it in the message's `diagramSpecs`. A Mermaid spec carries its
 * source; a whiteboard spec carries the canvas operations that build it. Nothing is saved until the
 * reader presses Save to Diagrams.
 */
export type DiagramSpec = (MermaidDiagramSpec | CanvasDiagramSpec) & {
  /**
   * The spec's place in the list the server stored, set when read from history. Links and saves
   * address a spec by it, so a spec this build cannot read never shifts the ones after it. A spec
   * that arrived on the stream has none; its place in the list is its position.
   */
  position?: number;
};

export interface MermaidDiagramSpec {
  format: 'cti-diagram';
  version: 1;
  kind: 'mermaid';
  title: string;
  source: string;
}

export interface CanvasDiagramSpec {
  format: 'cti-diagram';
  version: 1;
  kind: 'canvas';
  title: string;
  /** Canvas operations in the `edit_open_diagram` shape, checked one by one when the scene is built. */
  operations: DiagramSpecOperation[];
}

/** One canvas operation as the envelope carries it: an object with a string `op`, the rest unchecked. */
export type DiagramSpecOperation = { op: string } & Record<string, unknown>;

/** A spec of this message saved as a diagram, as the message view reports it. */
export interface DiagramLink {
  specIndex: number;
  diagramId: string;
}

const title = z.string().min(1).max(200);

/** The envelope checks conversation-service makes too; anything else is dropped. */
export const diagramSpecSchema: z.ZodType<DiagramSpec> = z.discriminatedUnion(
  'kind',
  [
    z.object({
      format: z.literal('cti-diagram'),
      version: z.literal(1),
      kind: z.literal('mermaid'),
      title,
      source: z.string().min(1).max(100_000),
    }),
    z.object({
      format: z.literal('cti-diagram'),
      version: z.literal(1),
      kind: z.literal('canvas'),
      title,
      operations: z
        .array(z.object({ op: z.string() }).passthrough())
        .min(1)
        .max(300),
    }),
  ],
);

const diagramLinkSchema = z.object({
  specIndex: z.number().int().nonnegative(),
  diagramId: z.string().min(1),
});

/** Parses one frame, or `null` when it is not a valid diagram envelope. */
export function parseDiagramSpec(raw: unknown): DiagramSpec | null {
  const result = diagramSpecSchema.safeParse(raw);
  return result.success ? result.data : null;
}

/** The valid envelopes of a stored list, in order. An absent or non-array field means none. */
export function parseDiagramSpecs(raw: unknown): DiagramSpec[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry, position) => {
    const spec = parseDiagramSpec(entry);
    return spec ? [{ ...spec, position }] : [];
  });
}

/** The well-formed links of a stored list. An absent or non-array field means none. */
export function parseDiagramLinks(raw: unknown): DiagramLink[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const result = diagramLinkSchema.safeParse(entry);
    return result.success ? [result.data] : [];
  });
}

/**
 * A spec that arrived on the stream, appended to a turn's list. The server appends it to its stored
 * list, so it takes the position after the last one already there, which keeps it clear of a stored
 * spec this build could not read.
 */
export function appendDiagramSpec(
  existing: DiagramSpec[] | undefined,
  spec: DiagramSpec,
): DiagramSpec[] {
  const specs = existing ?? [];
  const position = specs.reduce(
    (next, current, index) => Math.max(next, (current.position ?? index) + 1),
    0,
  );
  return [...specs, { ...spec, position }];
}
